import type {
  ActiveTab,
  BulkAction,
  Dialog,
  Filters,
  FiltersWithMultipleValues,
  Pagination,
  SingleAction,
  Sort,
  TabActionDialog,
} from '@common/types'
import { stringify as stringifyQs } from 'qs'
import urlJoin from 'url-join'

export const ticketSection = '/tickets/'

// List types
export const ticketListUserAssignedPath = 'assigned'
export const ticketListTeamAssignedPath = 'team-assigned'
export const ticketListAllPath = 'all'

export type TicketListType =
  typeof ticketListUserAssignedPath | typeof ticketListTeamAssignedPath | typeof ticketListAllPath

// Detail types
export const ticketDetailMediationPath = 'mediation'
export const ticketDetailNegotiationPath = 'negotiation'

export type TicketDetailType = typeof ticketDetailMediationPath | typeof ticketDetailNegotiationPath

export enum TicketsUrlFiltersEnum {
  closedFrom = 'closedFrom',
  closedTo = 'closedTo',
  contactAction = 'contactAction',
  createdFrom = 'createdFrom',
  createdTo = 'createdTo',
  phoneNumber = 'phoneNumber',
  query = 'query',
}

export enum TicketsUrlFiltersWithMultipleValuesEnum {
  assignedToUser = 'assignedToUser',
  assignedToUserTeam = 'assignedToUserTeam',
  closeReason = 'closeReason',
  product = 'product',
  status = 'status',
  tag = 'tag',
}

export enum TicketsUrlSortField {
  created = 'created',
  assignee = 'assigned',
}

export type TicketsUrlFilters = Filters<TicketsUrlFiltersEnum> &
  FiltersWithMultipleValues<TicketsUrlFiltersWithMultipleValuesEnum>

export type TicketsUrlDialog = TabActionDialog | 'assign' | 'close'

export type TicketsUrlSort = Sort<TicketsUrlSortField>
export type TicketsUrlQueryParams = BulkAction &
  TicketsUrlFilters &
  TicketsUrlSort &
  Pagination &
  ActiveTab &
  Dialog<TicketsUrlDialog> &
  SingleAction

export const ticketListUrl = (listType: TicketListType, params?: TicketsUrlQueryParams): string => {
  const ticketList = urlJoin(ticketSection, listType)
  if (params === undefined) {
    return ticketList
  } else {
    return urlJoin(ticketList, '?' + stringifyQs(params))
  }
}

// Ticket list URLs
export const ticketListUserAssignedUrl = (params?: TicketsUrlQueryParams) =>
  ticketListUrl(ticketListUserAssignedPath, params)

export const ticketListTeamAssignedUrl = (params?: TicketsUrlQueryParams) =>
  ticketListUrl(ticketListTeamAssignedPath, params)

export const ticketListAllUrl = (params?: TicketsUrlQueryParams) =>
  ticketListUrl(ticketListAllPath, params)

export const ticketDetailPath = (
  detailType: TicketDetailType,
  listType: TicketListType | undefined,
  id: string,
) => urlJoin([ticketSection, detailType, ...(listType ? [listType] : []), id])

export type TicketUrlDialog =
  | 'action-add'
  | 'action-update'
  | 'action-delete'
  | 'assign'
  | 'close'
  | 'outstanding-debt-update'
  | 'settlement-update'
  | 'ticket-document-add'
  | 'ticket-document-edit'
  | 'ticket-document-delete'

export type TicketUrlQueryParams = Dialog<TicketUrlDialog> &
  SingleAction &
  Partial<{ tab: string; historyFilter: string; documentType: string }>
export const ticketDetailUrl = (
  detailType: TicketDetailType,
  listType: TicketListType | undefined,
  id: string,
  params?: TicketUrlQueryParams,
) => ticketDetailPath(detailType, listType, encodeURIComponent(id)) + '?' + stringifyQs(params)

export const getTicketDetailUrl = (
  ticket: {
    __typename: 'MediationTicket' | 'NegotiationTicket'
    id: string
  },
  listType: TicketListType | undefined,
) => {
  switch (ticket.__typename) {
    case 'MediationTicket':
      return ticketDetailUrl('mediation', listType, ticket.id)
    case 'NegotiationTicket':
      return ticketDetailUrl('negotiation', listType, ticket.id)
    default:
      throw new Error(`Unsupported ticket type: ${(ticket as { __typename: string }).__typename}`)
  }
}
